import type { ReactNode } from 'react';
import { MathJaxContext } from 'better-react-mathjax';
import { logger } from '@/lib/logger';
import { MATHJAX_FONTS_PATH, MATHJAX_SRC } from '@/lib/mathjaxAssets';

/**
 * `onError` is not optional in practice. Given none, `MathJaxContext` handles a
 * failed script load by THROWING from inside the load promise's own `.catch` —
 * nothing is left to handle the result, so it surfaces as an unhandled
 * rejection: a page-level error in PostHog error tracking, with no log naming
 * the URL that
 * failed. Self-hosting is what makes that path reachable, since the engine now
 * lives at a path only the `vite.config.ts` copy step creates.
 *
 * What arrives is the script tag's DOM `error` Event rather than an Error, and
 * the logger unwraps only `instanceof Error` — an Event serialises to `"{}"`,
 * so the log would carry no more than the tag. Name the failure ourselves.
 *
 * This quiets the CONTEXT's rejection only. Each mounted `<MathJax>` runs its
 * own `.catch` that rethrows, so once this provider has consumers a failed
 * engine still produces one unhandled rejection per expression on the page.
 */
function handleEngineLoadError(error: unknown) {
  logger.error(
    '[MathJax] engine failed to load',
    error instanceof Error ? error : new Error(`Could not load ${MATHJAX_SRC}`),
  );
}

/**
 * Wraps a subtree so `<MathJax>` inside it can typeset, with the engine and its
 * glyphs served from this origin rather than `MathJaxContext`'s jsDelivr
 * defaults — the same reason `public/fonts/typst` is vendored. See
 * `@/lib/mathjaxAssets`, which `vite.config.ts` reads the same paths from.
 *
 * Mount ONE of these: MathJax is a singleton in the browser, and
 * `MathJaxContext` throws when two of them disagree about the version.
 */
export function MathJaxProvider({ children }: { children: ReactNode }) {
  return (
    <MathJaxContext
      version={4}
      src={MATHJAX_SRC}
      config={{
        loader: { paths: { fonts: MATHJAX_FONTS_PATH } },
        // `fontCache: 'local'` puts each expression's glyph `<defs>` inside its
        // own `<svg>`. The default, `'global'`, hoists them into one shared
        // `<defs>` per page — which is smaller on screen and unusable for the
        // PDF: lifting one expression's `<svg>` out to hand to Typst leaves its
        // `<use>` elements pointing at defs that did not come with it, and the
        // equation compiles to a blank box with no error from either side.
        //
        // `linebreaks.inline = false` (ENG-1093) — MathJax v4's SVG output
        // auto-breaks inline expressions to fit the container width. In the
        // Hand-Calc panel that fragmented `C_D = D / (½ρU² · A_ref)` into
        // four stacked pieces inside a ~140px label column. The consumer's
        // wrapping `<span w-max whitespace-nowrap>` (see `PhysicsFormula`)
        // is enough to keep the whole expression on one line — no need for
        // MathJax to make its own break decisions.
        svg: { fontCache: 'local', linebreaks: { inline: false } },
      }}
      onError={handleEngineLoadError}
    >
      {children}
    </MathJaxContext>
  );
}
